// 手机端使用单列输入与可换行工具栏，长地址完整阅读；电脑保留原布局，避免窄屏遮住表单或操作。
// 手机将明细按记录展示，保留原行事件；有交互表头或复杂行时保留可滑动表格，避免丢失排序、勾选和操作。
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "../components/ui/sonner";
import { ScrollText, FileDown, ShieldAlert, CheckCircle2, Gavel } from "lucide-react";
import { fmtD, fmtDT } from "../lib/date";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const TYPE_LABEL = { in: ["入库", "In"], issue: ["出库→项目", "Issue"], direct: ["直送项目", "Direct"], transfer: ["调拨", "Transfer"], return: ["退料", "Return"], adjust: ["盘点调整", "Adjust"] };
const TYPE_COLOR = { in: "bg-emerald-50 text-emerald-700", issue: "bg-blue-50 text-blue-700", direct: "bg-indigo-50 text-indigo-700", transfer: "bg-amber-50 text-amber-700", return: "bg-teal-50 text-teal-700", adjust: "bg-red-50 text-red-700" };

export default function WarehouseAudit() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const [warehouses, setWarehouses] = useState([]);
  const [data, setData] = useState({ rows: [], actors: [], types: [] });
  const [f, setF] = useState({ warehouseId: "", actor: "", type: "", dateFrom: "", dateTo: "" });
  const [onlyAnomaly, setOnlyAnomaly] = useState(false);
  const [alerts, setAlerts] = useState({ rows: [], canReview: false, pending: 0 });
  const [note, setNote] = useState({});

  const loadAlerts = useCallback(() => {
    api.get("/warehouses/loss-alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const review = async (aid, decision) => {
    try {
      await api.post(`/warehouses/adjustments/${aid}/audit-review`, { decision, note: note[aid] || "" });
      toast.success(zh ? (decision === "verified" ? "已标记：已核实" : "已标记：需追责") : "Saved");
      setNote((n) => ({ ...n, [aid]: "" })); loadAlerts();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  useEffect(() => { api.get("/warehouses").then((r) => setWarehouses(r.data.warehouses || [])).catch(() => {}); }, [api]);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append("limit", "500");
    api.get(`/warehouses/operation-ledger?${p.toString()}`).then((r) => setData(r.data)).catch((e) => toast.error(e?.detailText || e.message));
  }, [api, f]);
  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
    try {
      const res = await api.get(`/warehouses/operation-ledger/export?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "warehouse_ledger.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const reset = () => setF({ warehouseId: "", actor: "", type: "", dateFrom: "", dateTo: "" });

  const rows = (onlyAnomaly ? data.rows.filter((r) => (r.flags || []).length) : data.rows);

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="warehouse-audit-page">
      <div className="flex items-center gap-2">
        <ScrollText className="h-6 w-6 text-brand-500" />
        <div>
          <h1 className="text-xl font-bold text-slate-900">{zh ? "仓库操作流水" : "Warehouse Operation Ledger"}</h1>
          <p className="text-sm text-slate-400">{zh ? "按仓库 / 操作人 / 类型 / 日期稽核每笔出入库与盘点调整" : "Audit every stock movement & adjustment"}</p>
        </div>
      </div>

      {/* 大额盘亏稽核闭环 */}
      {alerts.rows.length > 0 && (
        <div data-testid="wh-loss-alerts" className="bg-white rounded-xl border border-red-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-red-100 bg-red-50 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-red-600" />
            <span className="text-sm font-semibold text-red-800">{zh ? "大额盘亏稽核" : "Large-loss audit"}</span>
            <span className="text-[11px] text-red-500">{zh ? `待处理 ${alerts.pending} · 共 ${alerts.rows.length}` : `${alerts.pending} pending / ${alerts.rows.length}`}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {alerts.rows.map((a) => {
              const st = a.auditStatus;
              const badge = st === "verified" ? ["已核实", "Verified", "bg-emerald-100 text-emerald-700"]
                : st === "accountability" ? ["需追责", "Accountability", "bg-red-100 text-red-700"]
                : ["待处理", "Pending", "bg-amber-100 text-amber-700"];
              return (
                <div key={a.id} data-testid={`wh-loss-alert-${a.id}`} className="px-4 py-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-slate-700">
                      <span className="font-semibold text-red-600">{money(a.lossAmount)}</span> · {a.warehouseName} · {a.month}
                      <span className="text-[11px] text-slate-400 ml-2">{zh ? "审批人" : "By"} {a.approvedBy} · {fmtD(a.approvedAt)}</span>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${badge[2]}`}>{zh ? badge[0] : badge[1]}</span>
                  </div>
                  {(a.items || []).length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500">{a.items.map((it) => `${it.name} ${it.book}→${it.actual}`).join(" · ")}</div>
                  )}
                  {alerts.canReview && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <input value={note[a.id] || ""} onChange={(e) => setNote((n) => ({ ...n, [a.id]: e.target.value }))}
                        placeholder={zh ? "处理说明(选填)" : "Note"} data-testid={`wh-loss-note-${a.id}`}
                        className="text-xs border border-slate-200 rounded-md px-2 py-1 w-full sm:w-auto sm:flex-1 min-w-0" />
                      <Button data-testid={`wh-loss-verify-${a.id}`} size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => review(a.id, "verified")}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{zh ? "已核实" : "Verified"}</Button>
                      <Button data-testid={`wh-loss-account-${a.id}`} size="sm" className="h-8 bg-brand-500 hover:bg-brand-600 text-white text-xs" onClick={() => review(a.id, "accountability")}><Gavel className="h-3.5 w-3.5 mr-1" />{zh ? "需追责" : "Accountability"}</Button>
                    </div>
                  )}
                  {(a.auditHistory || []).length > 0 && (
                    <div className="mt-2 space-y-0.5" data-testid={`wh-loss-history-${a.id}`}>
                      {a.auditHistory.map((h, i) => (
                        <div key={i} className="text-[11px] text-slate-500 border-l-2 border-slate-200 pl-2">
                          {fmtDT(h.at)} · {h.by} · <span className={h.decision === "verified" ? "text-emerald-600" : "text-red-600"}>{zh ? (h.decision === "verified" ? "已核实" : "需追责") : h.decision}</span>{h.note ? ` · ${h.note}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div data-testid="wh-audit-filters" className="bg-white rounded-xl border border-slate-200 p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-2 items-end">
        <div className="col-span-2 md:col-span-1"><label className="text-[11px] text-slate-400">{zh ? "仓库" : "Warehouse"}</label>
          <select data-testid="wh-audit-warehouse" value={f.warehouseId} onChange={(e) => setF((x) => ({ ...x, warehouseId: e.target.value }))} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2">
            <option value="">{zh ? "全部" : "All"}</option>{warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select></div>
        <div><label className="text-[11px] text-slate-400">{zh ? "操作人" : "Actor"}</label>
          <select data-testid="wh-audit-actor" value={f.actor} onChange={(e) => setF((x) => ({ ...x, actor: e.target.value }))} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2">
            <option value="">{zh ? "全部" : "All"}</option>{data.actors.map((a) => <option key={a.username} value={a.username}>{a.name}</option>)}
          </select></div>
        <div><label className="text-[11px] text-slate-400">{zh ? "类型" : "Type"}</label>
          <select data-testid="wh-audit-type" value={f.type} onChange={(e) => setF((x) => ({ ...x, type: e.target.value }))} className="w-full h-9 text-sm border border-slate-200 rounded-md px-2">
            <option value="">{zh ? "全部" : "All"}</option>{(data.types || []).map((t) => <option key={t} value={t}>{zh ? (TYPE_LABEL[t] || [t])[0] : (TYPE_LABEL[t] || [t, t])[1]}</option>)}
          </select></div>
        <div><label className="text-[11px] text-slate-400">{zh ? "起始日" : "From"}</label><Input data-testid="wh-audit-from" type="date" value={f.dateFrom} onChange={(e) => setF((x) => ({ ...x, dateFrom: e.target.value }))} className="h-9 text-sm" /></div>
        <div><label className="text-[11px] text-slate-400">{zh ? "结束日" : "To"}</label><Input data-testid="wh-audit-to" type="date" value={f.dateTo} onChange={(e) => setF((x) => ({ ...x, dateTo: e.target.value }))} className="h-9 text-sm" /></div>
        <div className="flex gap-2">
          <Button data-testid="wh-audit-reset" size="sm" variant="outline" className="h-9 flex-1" onClick={reset}>{zh ? "重置" : "Reset"}</Button>
          <Button data-testid="wh-audit-export" size="sm" className="h-9 flex-1 bg-brand-500 hover:bg-brand-600 text-white" onClick={doExport}><FileDown className="h-3.5 w-3.5 mr-1" />Excel</Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs text-slate-400">{zh ? `共 ${rows.length} 条记录` : `${rows.length} records`}</span>
          <div className="flex items-center gap-3 text-[11px]">
            <span className="flex items-center gap-1 text-slate-400"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" />{zh ? "大额盘亏" : "Big loss"}</span>
            <span className="flex items-center gap-1 text-slate-400"><span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300" />{zh ? "深夜操作" : "Off-hours"}</span>
            <label className="flex items-center gap-1 cursor-pointer text-slate-600" data-testid="wh-audit-only-anomaly"><input type="checkbox" checked={onlyAnomaly} onChange={(e) => setOnlyAnomaly(e.target.checked)} />{zh ? "仅看异常" : "Anomalies only"}</label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <ResponsiveTable className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="text-left px-4 py-2">{zh ? "时间" : "Time"}</th>
              <th className="text-left px-4 py-2">{zh ? "类型" : "Type"}</th>
              <th className="text-left px-4 py-2">{zh ? "仓库" : "Warehouse"}</th>
              <th className="text-left px-4 py-2">{zh ? "材料" : "Material"}</th>
              <th className="text-right px-4 py-2">{zh ? "数量" : "Qty"}</th>
              <th className="text-right px-4 py-2">{zh ? "金额" : "Value"}</th>
              <th className="text-left px-4 py-2">{zh ? "项目" : "Project"}</th>
              <th className="text-left px-4 py-2">{zh ? "操作人" : "Actor"}</th>
              <th className="text-left px-4 py-2">{zh ? "备注" : "Note"}</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 ? <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400">{zh ? "无记录" : "No records"}</td></tr> :
                rows.map((r) => {
                  const big = (r.flags || []).includes("bigLoss");
                  const off = (r.flags || []).includes("offHours");
                  return (
                  <tr key={r.id} data-testid={`wh-audit-row-${r.id}`} className={big ? "bg-red-50" : off ? "bg-amber-50" : ""}>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">
                      {off && <span title={zh ? "深夜操作" : "Off-hours"} className="mr-1">🌙</span>}
                      {fmtDT(r.createdAt)}
                    </td>
                    <td className="px-4 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${TYPE_COLOR[r.type] || "bg-slate-100 text-slate-600"}`}>{zh ? (TYPE_LABEL[r.type] || [r.type])[0] : (TYPE_LABEL[r.type] || [r.type, r.type])[1]}</span></td>
                    <td className="px-4 py-2 text-slate-700">{r.warehouseName || "-"}</td>
                    <td className="px-4 py-2 text-slate-800">{big && <span title={zh ? "大额盘亏" : "Big loss"} className="mr-1 text-red-600">⚠</span>}{r.name}</td>
                    <td className={`px-4 py-2 text-right font-medium ${r.qty < 0 ? "text-red-600" : "text-slate-700"}`}>{r.qty}</td>
                    <td className={`px-4 py-2 text-right ${big ? "text-red-600 font-semibold" : "text-slate-500"}`}>{money(r.totalCost)}</td>
                    <td className="px-4 py-2 text-slate-500">{r.projectAddress || "-"}</td>
                    <td className="px-4 py-2 text-slate-700">{r.createdBy || "-"}</td>
                    <td className="px-4 py-2 text-slate-400">{r.note || "-"}</td>
                  </tr>
                  );
                })}
            </tbody>
          </ResponsiveTable>
        </div>
      </div>
    </div>
  );
}
